/**
 * The route tree, defined in code.
 *
 * Code-based rather than file-based routing: the file-based plugin generates a
 * `routeTree.gen.ts` that has to be committed and regenerated, which is one
 * more artefact that can be stale in a pull request. Twenty routes do not need
 * it.
 *
 * Guards live in `beforeLoad` and read `context.session`, which the provider
 * hands to the router. That is the reason `Session.ready` exists: a guard that
 * runs before the stored refresh token has been redeemed would bounce a
 * signed-in user to the login screen on every hard refresh.
 */

import {
  createRootRouteWithContext,
  createRoute,
  createRouter,
  redirect,
} from "@tanstack/react-router";

import type { Session } from "./auth/session";
import { AdminAuditPage } from "./pages/admin/AuditPage";
import { AdminJobsPage } from "./pages/admin/JobsPage";
import { AdminLayout } from "./pages/admin/AdminLayout";
import { AdminOrganizationsPage } from "./pages/admin/OrganizationsPage";
import { AdminOverviewPage } from "./pages/admin/OverviewPage";
import { AdminUsersPage } from "./pages/admin/UsersPage";
import { AnalyticsPage } from "./pages/site/AnalyticsPage";
import { AdminPlansPage } from "./pages/admin/PlansPage";
import { AppLayout } from "./pages/AppLayout";
import { BillingPage } from "./pages/BillingPage";
import { ConnectPage } from "./pages/site/ConnectPage";
import { ContentPage } from "./pages/site/ContentPage";
import { ConversationsPage } from "./pages/site/ConversationsPage";
import { QuestionsPage } from "./pages/site/QuestionsPage";
import { ForgotPasswordPage } from "./pages/ForgotPasswordPage";
import { LoginPage } from "./pages/LoginPage";
import { NotFoundPage } from "./pages/NotFoundPage";
import { PrivacyPage } from "./pages/PrivacyPage";
import { OverviewPage } from "./pages/site/OverviewPage";
import { RegisterPage } from "./pages/RegisterPage";
import { ResetPasswordPage } from "./pages/ResetPasswordPage";
import { RootShell } from "./pages/RootShell";
import { SiteLayout } from "./pages/site/SiteLayout";
import { SitesPage } from "./pages/SitesPage";
import { WidgetPage } from "./pages/site/WidgetPage";

export interface RouterContext {
  session: Session;
}

const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: RootShell,
  notFoundComponent: NotFoundPage,
});

/** Anything under here requires a signed-in account. */
function requireUser({ context, location }: { context: RouterContext; location: { href: string } }) {
  if (!context.session.user) {
    throw redirect({ to: "/login", search: { next: location.href } });
  }
}

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/login",
  // The return type is annotated so `next` is *optional*, not
  // "required but possibly undefined". Without it every `<Link to="/login">`
  // in the app is a type error demanding a search object.
  validateSearch: (search: Record<string, unknown>): { next?: string } => ({
    next: typeof search.next === "string" ? search.next : undefined,
  }),
  component: LoginPage,
});

const registerRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/register",
  component: RegisterPage,
});

/**
 * Both reachable signed in or out. A signed-in user who forgot which password
 * they used still needs the form, and bouncing them to /sites would hide it.
 */
const forgotPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/forgot-password",
  component: ForgotPasswordPage,
});

const resetPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/reset-password",
  validateSearch: (search: Record<string, unknown>): { token?: string } => ({
    token: typeof search.token === "string" && search.token ? search.token : undefined,
  }),
  component: ResetPasswordPage,
});

const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "app",
  beforeLoad: requireUser,
  component: AppLayout,
});

const indexRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/",
  beforeLoad: () => {
    throw redirect({ to: "/sites" });
  },
});

const sitesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/sites",
  component: SitesPage,
});

const billingRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/billing",
  component: BillingPage,
});

const privacyRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/privacy",
  component: PrivacyPage,
});

const siteRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/sites/$siteId",
  component: SiteLayout,
});

const siteIndexRoute = createRoute({
  getParentRoute: () => siteRoute,
  path: "/",
  component: OverviewPage,
});

const siteContentRoute = createRoute({
  getParentRoute: () => siteRoute,
  path: "content",
  component: ContentPage,
});

const siteConversationsRoute = createRoute({
  getParentRoute: () => siteRoute,
  path: "conversations",
  component: ConversationsPage,
});

const siteQuestionsRoute = createRoute({
  getParentRoute: () => siteRoute,
  path: "questions",
  component: QuestionsPage,
});

const siteAnalyticsRoute = createRoute({
  getParentRoute: () => siteRoute,
  path: "analytics",
  component: AnalyticsPage,
});

const siteWidgetRoute = createRoute({
  getParentRoute: () => siteRoute,
  path: "widget",
  component: WidgetPage,
});

const siteConnectRoute = createRoute({
  getParentRoute: () => siteRoute,
  path: "connect",
  component: ConnectPage,
});

/**
 * The admin surface.
 *
 * The guard here is convenience, not security: it stops a non-staff account
 * seeing a broken screen. The actual boundary is the API's router-level
 * superuser dependency, which returns 403 no matter what this client believes
 * — and which is what a test parametrised over every `/admin/*` route pins.
 */
const adminRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/admin",
  beforeLoad: ({ context }) => {
    if (!context.session.user?.is_superuser) throw redirect({ to: "/sites" });
  },
  component: AdminLayout,
});

const adminIndexRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: "/",
  component: AdminOverviewPage,
});

const adminOrganizationsRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: "organizations",
  component: AdminOrganizationsPage,
});

const adminUsersRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: "users",
  component: AdminUsersPage,
});

const adminJobsRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: "jobs",
  component: AdminJobsPage,
});

const adminPlansRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: "plans",
  component: AdminPlansPage,
});

const adminAuditRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: "audit",
  component: AdminAuditPage,
});

export const routeTree = rootRoute.addChildren([
  loginRoute,
  registerRoute,
  forgotPasswordRoute,
  resetPasswordRoute,
  appRoute.addChildren([
    indexRoute,
    sitesRoute,
    billingRoute,
    privacyRoute,
    siteRoute.addChildren([
      siteIndexRoute,
      siteContentRoute,
      siteConversationsRoute,
      siteQuestionsRoute,
      siteAnalyticsRoute,
      siteWidgetRoute,
      siteConnectRoute,
    ]),
    adminRoute.addChildren([
      adminIndexRoute,
      adminOrganizationsRoute,
      adminUsersRoute,
      adminJobsRoute,
      adminPlansRoute,
      adminAuditRoute,
    ]),
  ]),
]);

export function buildRouter(session: Session) {
  return createRouter({
    routeTree,
    context: { session },
    defaultPreload: "intent",
  });
}

declare module "@tanstack/react-router" {
  interface Register {
    router: ReturnType<typeof buildRouter>;
  }
}
